import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, FileCheck2, Link2, ShieldCheck } from 'lucide-react'
import { useDiagramStore } from '../../../stores/diagramStore'
import { summarizeAssuranceFromAttrs } from '../models/AssuranceAttrs'
import { useProcessCanvasLayerStore } from '../journey/processCanvasLayerStore'

interface AssuranceSelectionInspectorProps {
  selectedShapes: Array<{ id?: string; type?: string; attrs?: Record<string, unknown> }>
  selectionBounds: {
    x: number
    y: number
    width: number
    height: number
  }
}

const Chip: React.FC<{
  icon: React.ReactNode
  label: string
  value: number
  tone: string
}> = ({ icon, label, value, tone }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
  >
    {icon}
    <span className="tabular-nums">{value}</span>
    <span>{label}</span>
  </span>
)

/**
 * Floating inspector that surfaces controls / evidence / risk counts
 * for the currently-selected node, anchored below the selection box.
 * No-ops unless exactly one node is selected and it carries assurance
 * data in its `attrs`. Positioning mirrors ShapeToolbar so the two
 * floating UIs stack cleanly above + below the shape.
 */
export const AssuranceSelectionInspector: React.FC<AssuranceSelectionInspectorProps> = ({
  selectedShapes,
  selectionBounds,
}) => {
  const { t } = useTranslation('process')
  const stage = useDiagramStore((state) => state.stage)
  const complianceActive = useProcessCanvasLayerStore((s) => s.complianceActive)

  // Compliance-layer gate — hidden in process folders without the
  // Compliance track. See docs/product/process-user-journey.md §6.
  if (!complianceActive) return null
  if (selectedShapes.length !== 1) return null
  const node = selectedShapes[0]
  const attrs = (node?.attrs ?? {}) as Record<string, unknown>
  if (attrs.__isPoolTable) return null

  const summary = summarizeAssuranceFromAttrs(attrs)
  if (!summary.hasAnyData) return null

  const scale = stage?.scale ?? 1
  const offsetX = stage?.offsetX ?? 0
  const offsetY = stage?.offsetY ?? 0
  const anchorScreenX = selectionBounds.x * scale + offsetX
  const anchorScreenY = selectionBounds.y * scale + offsetY
  const widthScreen = selectionBounds.width * scale
  const heightScreen = selectionBounds.height * scale
  const inspectorX = anchorScreenX + widthScreen / 2
  const inspectorY = anchorScreenY + heightScreen + 14

  return (
    <div
      data-testid="tid.editor.assuranceSelectionInspector"
      className="absolute z-50 flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 shadow-[0_4px_14px_rgba(2,6,23,0.10)]"
      style={{
        left: inspectorX,
        top: inspectorY,
        transform: 'translateX(-50%)',
      }}
    >
      <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <ShieldCheck className="size-3.5" />
        {t('assuranceInspector.title')}
      </span>
      {summary.controls > 0 && (
        <Chip
          icon={<Link2 className="size-3" />}
          label={t('assuranceInspector.controls')}
          value={summary.controls}
          tone="bg-blue-50 text-blue-700"
        />
      )}
      {summary.evidence > 0 && (
        <Chip
          icon={<FileCheck2 className="size-3" />}
          label={t('assuranceInspector.evidence')}
          value={summary.evidence}
          tone="bg-emerald-50 text-emerald-700"
        />
      )}
      {summary.risks > 0 && (
        <Chip
          icon={<AlertTriangle className="size-3" />}
          label={t('assuranceInspector.risks')}
          value={summary.risks}
          tone="bg-red-50 text-red-700"
        />
      )}
      {summary.findings > 0 && (
        <Chip
          icon={<AlertTriangle className="size-3" />}
          label={t('assuranceInspector.findings')}
          value={summary.findings}
          tone="bg-amber-50 text-amber-800"
        />
      )}
    </div>
  )
}
